import { useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon, Tick01Icon } from "@hugeicons/core-free-icons"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { OnboardingAgentStep } from "@/components/onboarding/OnboardingAgentStep"
import { MCP_URL } from "@/lib/constants/agentConnect"
import AutoTopUpRows from "@/components/billing/AutoTopUpRows"
import { useAuth } from "@/lib/contexts/AuthContext"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import { useAutoTopUp } from "@/hooks/useAutoTopUp"
import { getAccountApiKeys, type ApiKey } from "@/lib/api/api-keys"
import { getAllProperties } from "@/lib/api/api"
import { getAllWorkspaces } from "@/lib/api/workspaces"
import { buyTopUp, createAgentsCheckout } from "@/lib/api/billing"
import { generateSlugId } from "@/lib/routing/slug-id"
import { track } from "@/lib/utils/track"
import { cn } from "@/lib/utils"
import HumblyticsLogo from "@/assets/humblytics-web-app-logo.svg"

/** Picks the setup back up when the agent session that signed the person up is gone. MCP OAuth works for an
 * existing account (agent.md sign-up does not), and the person is already signed in here, so it is one Allow. */
const RESUME_MESSAGE = `Connect the Humblytics MCP server at ${MCP_URL}, then set up Humblytics on my website.`
const compact = new Intl.NumberFormat("en", { notation: "compact" })
const DAY_MS = 24 * 60 * 60 * 1000

type SiteSummary = { propertyId: string; domain: string; name?: string }

function lastUsedLabel(lastUsedAt: string | null): string {
  if (!lastUsedAt) return "Not used yet"
  const minutes = Math.round((Date.now() - Date.parse(lastUsedAt)) / 60_000)
  if (minutes < 1) return "Used just now"
  if (minutes < 60) return `Used ${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Used ${hours} h ago`
  return `Used ${new Date(lastUsedAt).toLocaleDateString([], { month: "short", day: "numeric" })}`
}

/** Settings · Account card anatomy: title row over hairline-divided rows. */
function Section({ title, meta, action, children }: { title: string; meta?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="surface-raised flex flex-col overflow-clip">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-[18px]">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <h2 className="text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
          {meta ? <p className="text-xs leading-4 text-foreground/60 tabular-nums">{meta}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function CopyChip({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 pl-3 shadow-[0_0_0_1px_var(--border)]">
      <span className="min-w-0 flex-1 text-xs leading-[18px] text-foreground">{text}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          } catch {
            toast.error("Couldn't copy. Select the text and copy it instead.")
          }
        }}
        className="h-8 shrink-0 gap-1.5 rounded-lg bg-card px-3 text-xs"
      >
        <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={14} />
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  )
}

/**
 * Home for agent-plan accounts (Free start, Agents). The agent does the work
 * through MCP; this page is the account: which agents are connected, the
 * data-point balance and top-ups, the sites, and links to keys, connectors
 * and settings.
 */
export default function AgentHome() {
  const { token } = useAuth()
  const { planKind } = useSubscription()
  const { autoTopUp, topUps, dataPoints, save, saving, refresh } = useAutoTopUp()
  const [connecting, setConnecting] = useState(false)
  const [checkingOut, setCheckingOut] = useState(false)
  const [toppingUp, setToppingUp] = useState(false)

  useEffect(() => {
    document.title = "Agent - Humblytics"
  }, [])

  const keysQuery = useQuery({
    queryKey: ["account-api-keys", token],
    queryFn: () => getAccountApiKeys(token as string),
    enabled: !!token,
    refetchInterval: connecting ? 5_000 : false,
  })
  const sitesQuery = useQuery({
    queryKey: ["properties-all", token],
    queryFn: () => getAllProperties(token as string),
    enabled: !!token,
  })
  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces", token],
    queryFn: () => (token ? getAllWorkspaces(token) : []),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })

  const keys: ApiKey[] = (keysQuery.data || []).filter((k) => k.isActive && k.scope === "account")
  const sites: SiteSummary[] = ((sitesQuery.data as { properties?: SiteSummary[] } | undefined)?.properties || []).filter(
    (p) => p.domain,
  )
  // The connect step opens only when asked: agent sign-ups arrive from their agent, so they get the
  // placeholder below instead (Paper "04 Agent flow", founder 2026-11-02).
  const showConnect = connecting
  const awaitingAgent = !connecting && !sitesQuery.isLoading && sites.length === 0

  const workspace = workspaces[0]
  const workspaceBase = workspace
    ? `/workspace/${generateSlugId(workspace.name || "workspace", workspace.workspaceId)}`
    : null

  const onFreeStart = planKind === "free_start"
  const balanceMeta = dataPoints
    ? `${compact.format(dataPoints.used)} of ${compact.format(dataPoints.limit)} data points used · ${compact.format(dataPoints.remaining)} left`
    : undefined
  const usedPct = dataPoints && dataPoints.limit > 0 ? Math.min(100, (dataPoints.used / dataPoints.limit) * 100) : 0
  const outOfData =
    dataPoints?.status === "paused" || dataPoints?.status === "past_limit" || dataPoints?.status === "payment_failed"
  const balanceTone = outOfData ? "warning" : null
  const runningLow = dataPoints?.status === "approaching_limit"
  const blockLabel = topUps ? compact.format(topUps.blockPoints) : "100K"

  const startCheckout = async () => {
    if (!token) return
    setCheckingOut(true)
    try {
      track("agents_checkout_clicked", token, { surface: "agent_home" })
      const { url } = await createAgentsCheckout(token, {
        successPath: "/agent-complete?session_id={CHECKOUT_SESSION_ID}",
        cancelPath: "/agent",
      })
      window.location.assign(url)
    } catch {
      toast.error("Couldn't open checkout", { description: "Try again in a moment." })
      setCheckingOut(false)
    }
  }

  const startTopUp = async () => {
    if (!token) return
    setToppingUp(true)
    try {
      track("agents_top_up_clicked", token, { surface: "agent_home", status: dataPoints?.status })
      const res = await buyTopUp(token, 1)
      toast.success(`Added ${compact.format(res.points)} data points`, {
        description: "Tracking is on. They are valid for 12 months.",
      })
      await refresh()
    } catch (err) {
      const code = (err as { code?: string }).code
      toast.error("Couldn't add data points", {
        description:
          code === "requires_action" || code === "no_payment_method"
            ? "Your card needs to be confirmed. Update it in Account settings, then try again."
            : code === "payment_failed"
              ? "Your card was declined. Update it in Account settings, then try again."
              : "Try again in a moment.",
      })
    } finally {
      setToppingUp(false)
    }
  }

  // One upgrade action per plan: Free start buys the Agents plan, Agents buys a top-up.
  const upgrade = onFreeStart
    ? { label: "Get 100K for $39", busy: checkingOut, run: startCheckout }
    : topUps
      ? { label: `Add ${blockLabel} for $29`, busy: toppingUp, run: startTopUp }
      : null

  return (
    <div className="min-h-svh bg-canvas text-foreground">
      <header className="flex h-[72px] items-center justify-between px-4 sm:px-8">
        <Link to="/agent" className="inline-flex items-center gap-2">
          <img src={HumblyticsLogo} alt="" className="size-5" />
          <span className="text-base font-medium leading-5 tracking-[-0.01em]">Humblytics</span>
        </Link>
        <Link to="/user-settings" className="text-[13px] font-medium text-foreground/70 hover:text-foreground">
          Account settings
        </Link>
      </header>

      <main className="mx-auto flex w-full max-w-[880px] flex-col gap-4 px-4 pb-16 sm:px-8">
        {(outOfData || runningLow) && upgrade ? (
          <section
            role={outOfData ? "alert" : "status"}
            className="surface-raised flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 flex-col gap-1">
              <h2 className="text-sm font-semibold leading-[18px]">
                {outOfData
                  ? onFreeStart
                    ? "Your 25,000 free data points are used up"
                    : "Your data points are used up"
                  : "You're running low on data points"}
              </h2>
              <p className="text-xs leading-4 text-foreground/60">
                {outOfData
                  ? "Tracking is paused, so new visits are not recorded. Your data and agent access stay."
                  : `${compact.format(dataPoints?.remaining ?? 0)} left. Tracking pauses when they run out.`}
              </p>
            </div>
            <Button
              size="sm"
              className="h-[34px] shrink-0 rounded-lg text-xs"
              disabled={upgrade.busy}
              onClick={() => void upgrade.run()}
            >
              {upgrade.label}
            </Button>
          </section>
        ) : null}
        {awaitingAgent ? (
          <section className="surface-raised flex flex-col gap-4 p-5 sm:p-8">
            <div className="flex flex-col gap-1.5">
              <h1 className="text-[22px] font-medium leading-7 tracking-[-0.01em]">Your account is ready. Finish in your agent.</h1>
              <p className="text-[13px] leading-5 text-foreground/60">
                Your agent adds your site and installs the script. It asks before it publishes anything.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
              <p className="text-[13px] font-medium leading-5">Closed it? Paste this into any agent to pick up where you left off.</p>
              <CopyChip text={RESUME_MESSAGE} />
            </div>
            <p className="text-[13px] leading-5 text-foreground/60">
              Stuck? Ask your agent to email{" "}
              <a href="mailto:support@humblytics.com" className="font-medium text-foreground underline-offset-2 hover:underline">
                support@humblytics.com
              </a>{" "}
              with what it tried, or write to us directly.
            </p>
          </section>
        ) : null}
        {showConnect ? (
          <section className="surface-raised p-5 sm:p-8">
            <OnboardingAgentStep
              token={token}
              installNext={sites.length === 0}
              dashboardLabel="Done"
              onPromptCopied={(client) => track("agent_home_connect_copied", token, { client })}
              onFinish={({ connected }) => {
                setConnecting(false)
                if (connected) void keysQuery.refetch()
              }}
            />
          </section>
        ) : null}

        <Section
          title="Agents"
          meta={keysQuery.isLoading ? undefined : `${keys.length} connected`}
          action={
            !showConnect ? (
              <Button variant="outline" size="sm" className="h-[34px] rounded-lg text-xs" onClick={() => setConnecting(true)}>
                Connect another agent
              </Button>
            ) : null
          }
        >
          {keysQuery.isLoading ? (
            <div className="px-5 py-[18px]">
              <Skeleton className="h-4 w-48" />
            </div>
          ) : keys.length === 0 ? (
            <p className="px-5 py-[18px] text-xs text-foreground/60">No agent connected</p>
          ) : (
            <ul>
              {keys.map((key, i) => {
                const live = key.lastUsedAt && Date.now() - Date.parse(key.lastUsedAt) < DAY_MS
                return (
                  <li key={key.id} className={cn("flex items-center justify-between gap-4 px-5 py-3.5", i > 0 && "border-t border-border")}>
                    <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium">
                      <span className={cn("size-1.5 shrink-0 rounded-full", live ? "bg-live" : "bg-foreground/25")} />
                      <span className="truncate">{key.name}</span>
                    </span>
                    <span className="shrink-0 text-xs text-foreground/60 tabular-nums">{lastUsedLabel(key.lastUsedAt)}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </Section>

        <Section
          title={onFreeStart ? "Free start" : "Agents plan"}
          meta={balanceMeta}
          action={
            upgrade && !(outOfData || runningLow) ? (
              <Button
                size="sm"
                variant={onFreeStart ? "default" : "outline"}
                className="h-[34px] rounded-lg text-xs"
                disabled={upgrade.busy}
                onClick={() => void upgrade.run()}
              >
                {upgrade.label}
              </Button>
            ) : null
          }
        >
          {dataPoints ? (
            <div className="flex flex-col gap-2 px-5 py-[18px]">
              <div
                className="h-1.5 rounded-[2px] bg-[#F2F2F2] dark:bg-white/10"
                role="progressbar"
                aria-label="Data points used"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(usedPct)}
              >
                <div className="h-1.5 rounded-[2px] bg-foreground" style={{ width: `${usedPct}%` }} />
              </div>
              {balanceTone ? (
                <span className="flex items-center gap-2 text-xs font-medium text-warning">
                  <span className="size-1.5 rounded-full bg-warning" />
                  {dataPoints.trackingPaused ? "Tracking paused" : "Out of data points"}
                </span>
              ) : null}
            </div>
          ) : (
            <div className="px-5 py-[18px]">
              <Skeleton className="h-1.5 w-full" />
            </div>
          )}
          {autoTopUp ? (
            <div className="border-t border-border">
              <AutoTopUpRows autoTopUp={autoTopUp} save={save} saving={saving} />
            </div>
          ) : null}
        </Section>

        <Section title="Sites" meta={sitesQuery.isLoading ? undefined : `${sites.length}`}>
          {sitesQuery.isLoading ? (
            <div className="px-5 py-[18px]">
              <Skeleton className="h-4 w-40" />
            </div>
          ) : sites.length === 0 ? (
            <div className="flex flex-col gap-3 px-5 py-[18px]">
              <p className="text-xs text-foreground/60">No sites yet. Your agent adds them.</p>
            </div>
          ) : (
            <ul>
              {sites.map((site, i) => (
                <li key={site.propertyId} className={cn("flex items-center justify-between gap-4 px-5 py-3.5", i > 0 && "border-t border-border")}>
                  <span className="truncate text-[13px] font-medium">{site.domain}</span>
                  {site.name && site.name !== site.domain ? (
                    <span className="truncate text-xs text-foreground/60">{site.name}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <nav aria-label="Account" className="flex flex-wrap gap-2 pt-2">
          {workspaceBase ? (
            <>
              <Button asChild variant="outline" size="sm" className="h-[34px] rounded-lg bg-card text-xs">
                <Link to={`${workspaceBase}/api`}>API keys &amp; MCP</Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="h-[34px] rounded-lg bg-card text-xs">
                <Link to={`${workspaceBase}/connectors`}>Connectors</Link>
              </Button>
            </>
          ) : null}
          <Button asChild variant="outline" size="sm" className="h-[34px] rounded-lg bg-card text-xs">
            <Link to="/user-settings">Account settings</Link>
          </Button>
        </nav>
      </main>
    </div>
  )
}
